import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Buildings, Warning, CheckCircle, XCircle } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const usd = c => `$${((c || 0) / 100).toFixed(2)}`;

const STATUS_LABEL = {
  active: "Active",
  pending_rehab_decision: "Awaiting Rehab Decision",
  vacant: "Vacant",
};

function PropertyCard({ p, token }) {
  const pending = p.status === "pending_rehab_decision";
  const isWarehouse = p.warehouse_count > 0;
  const daysSinceCheck = p.last_check_at ? Math.floor((Date.now() - new Date(p.last_check_at).getTime()) / 86400000) : null;
  const cadence = p.check_cadence_days || 0;
  const overdue = cadence > 0 && daysSinceCheck !== null && daysSinceCheck >= cadence;
  const dueSoon = cadence > 0 && daysSinceCheck !== null && daysSinceCheck >= cadence - 3 && !overdue;
  return (
    <a href={`/landlord/${token}/property/${encodeURIComponent(p.address_key)}`}
      className={`block p-3 bg-white border-2 ${pending ? "border-amber-500" : overdue ? "border-red-500" : isWarehouse ? "border-slate-600" : "border-[#0f172a]"} hover:shadow-[4px_4px_0px_#0f172a] transition-shadow`}
      data-testid={`property-${p.address_key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-black text-lg leading-tight uppercase tracking-tight truncate">
            {isWarehouse && "🏭 "}{p.address}
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-0.5">
            {isWarehouse
              ? <>Vacant Warehouse{cadence > 0 && <span className="text-slate-600"> · Check every {cadence} days</span>}</>
              : <>
                  {p.unit_count - (p.commercial_count || 0)} residential
                  {p.commercial_count > 0 && <span className="text-purple-600"> · {p.commercial_count} commercial</span>}
                  {p.occupied_count > 0 && ` · ${p.occupied_count} occupied`}
                  {p.signed_count > 0 && <span className="text-blue-600"> · {p.signed_count} signed</span>}
                  {p.unit_count > 0 && (p.unit_count - p.occupied_count - p.signed_count) > 0 && !pending && (
                    <span className="text-amber-600"> · {p.unit_count - p.occupied_count - p.signed_count} vacant</span>
                  )}
                </>
            }
          </div>
          {isWarehouse && daysSinceCheck !== null && (
            <div className={`mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${overdue ? "bg-red-600 text-white animate-pulse" : dueSoon ? "bg-amber-500 text-white" : "bg-emerald-100 text-emerald-800 border border-emerald-300"}`} data-testid={`warehouse-check-${p.address_key}`}>
              {overdue ? `⚠️ Overdue — ${daysSinceCheck}d since last` : dueSoon ? `⏰ Due soon — ${daysSinceCheck}d since last` : `✓ Last checked ${daysSinceCheck}d ago`}
            </div>
          )}
          {p.head_lessee && (
            <div className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 border border-cyan-300" data-testid={`property-lessee-${p.address_key}`}>
              🎓 Leased to {p.head_lessee}
            </div>
          )}
        </div>
        {pending && (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-500 text-white shrink-0">⏳ Rehab Pending</span>
        )}
      </div>
    </a>
  );
}

function PendingItem({ item, cap, onDecide }) {
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const overCap = item.cost_cents > cap;
  const decide = async (decision) => {
    setBusy(true);
    try {
      await onDecide(item.project_id, item.id, decision, note);
      toast.success(`${decision === "approved" ? "✓ Approved" : "✗ Rejected"} — ${item.title.slice(0, 30)}…`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't record decision")); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-3 bg-white border-2 border-[#0f172a] border-l-8 border-l-orange-500" data-testid={`pending-${item.id}`}>
      <div className="flex items-center gap-1 flex-wrap mb-1">
        <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-orange-500 text-white animate-pulse">⏳ Needs Your Approval</span>
        {overCap && <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-100 text-red-700">Over ${cap/100}</span>}
        <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700">{item.room}</span>
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{item.category}</span>
      </div>
      <div className="font-black text-sm">{item.title}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{item.address} · {item.project_name}</div>
      {item.description && <div className="text-xs text-slate-600 italic mt-1">{item.description}</div>}
      {item.quote_status === "needed" ? (
        <div className="mt-2">
          <div className="inline-block font-display font-black text-lg text-amber-600 bg-amber-100 px-2 py-1 border-2 border-amber-500 uppercase tracking-widest">📋 Quote Needed</div>
        </div>
      ) : (
        <div className="mt-2 font-display font-black text-2xl text-orange-600">{usd(item.cost_cents)}</div>
      )}
      {showNote && (
        <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
          placeholder="Note for the property manager (optional)…"
          className="mt-2 w-full px-2 py-1 border-2 border-slate-300 text-xs resize-none"/>
      )}
      <div className="mt-2 flex gap-2 flex-wrap">
        <button onClick={() => decide("approved")} disabled={busy} data-testid={`approve-${item.id}`}
          className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-1 disabled:opacity-40">
          <CheckCircle weight="bold" size={14}/> Approve
        </button>
        <button onClick={() => decide("rejected")} disabled={busy} data-testid={`reject-${item.id}`}
          className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-1 disabled:opacity-40">
          <XCircle weight="bold" size={14}/> Reject
        </button>
        <button onClick={() => setShowNote(v => !v)}
          className="h-10 px-3 bg-white hover:bg-slate-50 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest">
          {showNote ? "Hide note" : "+ Add note"}
        </button>
      </div>
    </div>
  );
}

export default function LandlordPortalView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pf, pn] = await Promise.all([
        axios.get(`${BASE}/api/public/landlord/${token}`),
        axios.get(`${BASE}/api/public/landlord/${token}/pending`),
      ]);
      setData(pf.data);
      setPending(pn.data.pending || []);
    } catch { toast.error("Invalid or expired link"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  const decide = async (project_id, item_id, decision, note) => {
    await axios.post(`${BASE}/api/public/landlord/${token}/decide`, { project_id, item_id, decision, note });
    setPending(p => p.filter(x => x.id !== item_id));
  };

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-400 text-xs">Loading…</div>;
  if (!data) return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-black uppercase text-slate-500">Invalid link</div>;

  const totalUnits = (data.properties || []).reduce((s, p) => s + p.unit_count, 0);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="landlord-portal">
      <header className="text-white" style={{ background: data.brand_color || "#0f172a" }} data-testid="landlord-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {data.brand_logo && (
              <img src={data.brand_logo} alt={data.landlord_name}
                data-testid="landlord-brand-logo"
                className="h-14 sm:h-16 max-w-[260px] object-contain bg-white/95 border-2 border-white/70 px-2 py-1"/>
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-500">Portfolio Portal · Read-Only Decisions</div>
              <div className="font-display font-black text-2xl truncate" data-testid="landlord-name">{data.landlord_name}</div>
              <div className="text-xs text-slate-300 mt-0.5">
                {(data.properties || []).length} {(data.properties || []).length === 1 ? "property" : "properties"} · {totalUnits} units total
              </div>
            </div>
          </div>
          <a href={`/landlord/${token}/inbox`} data-testid="header-inbox-link"
            className={`px-3 h-11 inline-flex items-center gap-2 border-2 border-amber-500 font-black uppercase text-[10px] tracking-widest ${
              pending.length > 0 ? "bg-orange-500 text-white animate-pulse" : "bg-white/10 text-amber-500 hover:bg-white/20"
            }`}>
            📥 Approval Inbox
            {pending.length > 0 && (
              <span className="px-1.5 py-0.5 bg-white text-orange-600 text-[10px] font-black">{pending.length}</span>
            )}
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Alarm Addendums — landlord signs here instead of email reply */}
        <AlarmAddendumSection token={token}/>

        {/* Leasing team — landlord invites their own leasing agents */}
        <LandlordLeasingSection token={token}/>

        {/* Pending approvals — always up top */}
        <section data-testid="pending-approvals">
          <div className="flex items-center gap-2 mb-2">
            <Warning weight="fill" size={22} className="text-orange-500"/>
            <div className="font-display font-black text-xl uppercase tracking-tight">Pending Your Approval</div>
            {pending.length > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest bg-orange-500 text-white animate-pulse">{pending.length}</span>
            )}
          </div>
          {pending.length === 0 ? (
            <div className="p-6 bg-white border-2 border-dashed border-emerald-500 text-center">
              <CheckCircle weight="fill" size={36} className="mx-auto text-emerald-500"/>
              <div className="mt-2 font-black uppercase tracking-widest text-sm">Nothing waiting on you</div>
              <div className="text-xs text-slate-500 mt-1">Any new discovery over ${data.approver_cap_cents/100} will show up here for your call.</div>
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {pending.map(it => <PendingItem key={it.id} item={it} cap={data.approver_cap_cents} onDecide={decide}/>)}
            </div>
          )}
        </section>

        {/* Properties */}
        <section data-testid="properties-list">
          <div className="flex items-center gap-2 mb-2">
            <Buildings weight="fill" size={22} className="text-fuchsia-600"/>
            <div className="font-display font-black text-xl uppercase tracking-tight">Your Properties</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[70vh] overflow-y-auto">
            {(data.properties || []).map(p => <PropertyCard key={p.address_key} p={p} token={token}/>)}
          </div>
        </section>

        {/* Active projects */}
        {(data.projects || []).length > 0 && (
          <section data-testid="projects-list">
            <div className="flex items-center gap-2 mb-2">
              <div className="font-display font-black text-xl uppercase tracking-tight">Active Projects</div>
            </div>
            <div className="space-y-2">
              {data.projects.map(p => {
                const pct = p.total > 0 ? Math.round((p.complete / p.total) * 100) : 0;
                return (
                  <div key={p.id} className="p-3 bg-white border-2 border-[#0f172a]">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <div className="font-black text-sm">{p.name}</div>
                        <div className="text-xs text-slate-600">{p.address}</div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">
                          {p.complete}/{p.total} done · {pct}%
                        </div>
                      </div>
                      {p.share_token && (
                        <a href={`/project/${p.share_token}`} target="_blank" rel="noreferrer"
                          className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center">
                          🔗 View Live Status
                        </a>
                      )}
                    </div>
                    <div className="mt-2 h-2 bg-slate-100 border border-slate-300">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 text-center pt-4">
          Powered by Field Radar · Portfolio View
        </div>
      </div>
    </div>
  );
}



// ────────────────────────────────────────────────────────────────────
// Alarm Addendum ack section — lives on the landlord portal at the top.
// Shows every property with an addendum pending signature and gives a
// big green "I Acknowledge" button that writes straight to the audit log.
function AlarmAddendumSection({ token }) {
  const [rows, setRows] = useState(null);
  const [signing, setSigning] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${BASE}/api/public/landlord/${token}/alarm-addendums`);
      setRows(r.data?.addendums || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, [token]);

  const sign = async (row) => {
    if (!window.confirm(
      `Acknowledge the alarm addendum for ${row.address}${row.unit ? ' · Unit ' + row.unit : ''}?\n\n` +
      `Your name and timestamp will be recorded in the audit log. This is the equivalent of signing.`
    )) return;
    setSigning(row.property_id);
    try {
      await axios.post(`${BASE}/api/public/landlord/${token}/alarm-addendum/${row.property_id}/acknowledge`);
      toast.success("✍️ Acknowledged — signature logged");
      await load();
    } catch {
      toast.error("Couldn't record signature — try again");
    } finally { setSigning(""); }
  };

  if (rows === null || rows.length === 0) return null;
  const pending = rows.filter(r => !r.acknowledged_at);
  const signed = rows.filter(r => r.acknowledged_at);
  return (
    <section data-testid="alarm-addendum-section" id="alarm-addendum-section">
      <div className="flex items-center gap-2 mb-2">
        <div className="font-display font-black text-xl uppercase tracking-tight">📄 Alarm Install Addendums</div>
        {pending.length > 0 && (
          <span className="px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest bg-red-600 text-white animate-pulse">
            {pending.length} awaiting your sign-off
          </span>
        )}
      </div>
      <div className="space-y-2">
        {pending.map(r => (
          <div key={r.property_id} id={`alarm-addendum-${r.property_id}`}
            className="p-3 bg-white border-2 border-red-600" data-testid={`addendum-row-${r.property_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-red-700">Alarm Install · Terms &amp; Charges</div>
                <div className="font-display font-black text-base uppercase tracking-tight mt-0.5">
                  {r.address}{r.unit ? ` · Unit ${r.unit}` : ""}
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-0.5">
                  Sent {r.sent_at ? new Date(r.sent_at).toLocaleDateString() : "—"} · $450 install · $45/mo · $125 false-alarm trip
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <a href={`${BASE}${r.pdf_url}`} target="_blank" rel="noreferrer"
                  data-testid={`addendum-pdf-${r.property_id}`}
                  className="h-11 px-4 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                  📄 Review PDF
                </a>
                <button onClick={() => sign(r)} disabled={signing === r.property_id}
                  data-testid={`addendum-sign-${r.property_id}`}
                  className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
                  {signing === r.property_id ? "Signing…" : "✅ I Acknowledge"}
                </button>
              </div>
            </div>
          </div>
        ))}
        {signed.length > 0 && (
          <details className="p-2 bg-slate-50 border border-slate-200 text-xs">
            <summary className="cursor-pointer font-black uppercase tracking-widest text-slate-500 text-[10px]">
              ✓ {signed.length} previously signed
            </summary>
            <div className="mt-2 space-y-1">
              {signed.map(r => (
                <div key={r.property_id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{r.address}{r.unit ? ` · ${r.unit}` : ""}</span>
                  <span className="text-[10px] text-emerald-700 font-black">
                    ✓ {r.acknowledged_at?.slice(0,10)}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </section>
  );
}

// ─── Landlord-scoped leasing team ─────────────────────────────────
// Owner (Irfan/Alex) can invite their own leasing agents from here.
// Each agent is auto-scoped to only this landlord's units (backend
// forces `landlord_id` to this owner). Also shows recent reports +
// showings + messages so owner sees what their leasing team is doing.
function LandlordLeasingSection({ token }) {
  const [agents, setAgents] = useState(null);
  const [feed, setFeed] = useState({ reports: [], showings: [], messages: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const [a, f] = await Promise.all([
        axios.get(`${BASE}/api/public/landlord/${token}/leasing/agents`),
        axios.get(`${BASE}/api/public/landlord/${token}/leasing/feed`),
      ]);
      setAgents(a.data.agents || []);
      setFeed(f.data || { reports: [], showings: [], messages: [] });
    } catch { setAgents([]); }
  };
  useEffect(() => { load(); }, [token]);

  if (agents === null) return null;
  const activityCount = (feed.reports?.length || 0) + (feed.showings?.length || 0);

  return (
    <section data-testid="landlord-leasing-section">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="font-display font-black text-xl uppercase tracking-tight">🔑 Your Leasing Team</div>
        {agents.length > 0 && (
          <span className="px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest bg-cyan-600 text-white">
            {agents.length} {agents.length === 1 ? "agent" : "agents"}
          </span>
        )}
        {activityCount > 0 && (
          <span className="px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest bg-emerald-600 text-white">
            {activityCount} recent
          </span>
        )}
        <button onClick={() => setShowAdd(true)}
          className="ml-auto h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1"
          data-testid="landlord-leasing-add-btn">
          + Invite Leasing Agent
        </button>
      </div>

      {agents.length === 0 ? (
        <div className="p-4 bg-white border-2 border-dashed border-cyan-500 text-center">
          <div className="font-black uppercase tracking-widest text-sm">No leasing agents yet</div>
          <div className="text-xs text-slate-500 mt-1">
            Give your leasing team a private link with entry codes, alarm codes, and a report button — no more texting codes back and forth.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {agents.map(a => <LandlordAgentRow key={a.id} a={a}/>)}
        </div>
      )}

      {activityCount > 0 && (
        <div className="mt-3">
          <button onClick={() => setExpanded(v => !v)}
            className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800"
            data-testid="landlord-leasing-feed-toggle">
            {expanded ? "▼" : "▶"} Recent activity ({activityCount})
          </button>
          {expanded && <LandlordLeasingFeed feed={feed}/>}
        </div>
      )}

      {showAdd && <LandlordLeasingAddModal token={token} onClose={() => setShowAdd(false)} onCreated={load}/>}
    </section>
  );
}

function LandlordAgentRow({ a }) {
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(a.url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const smsHref = a.phone
    ? `sms:${a.phone}?body=${encodeURIComponent(`Hi ${a.name || ""} — your leasing portal is ready: ${a.url}`)}`
    : `sms:?body=${encodeURIComponent(`Hi ${a.name || ""} — your leasing portal is ready: ${a.url}`)}`;
  const mailHref = `mailto:${a.email || ""}?subject=${encodeURIComponent("Your leasing portal")}&body=${encodeURIComponent(`Hi ${a.name || ""},\n\nYour private leasing portal is ready. Bookmark this link on your phone:\n\n${a.url}\n\nIt shows every vacant unit with entry codes, alarm codes, and a report button.\n`)}`;
  return (
    <div className="p-3 bg-white border-2 border-slate-200" data-testid={`landlord-leasing-agent-${a.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-black text-sm shrink-0">
          {(a.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-black text-sm">{a.name}</div>
            {a.active !== false ? (
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5">Active</span>
            ) : (
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 bg-slate-100 px-1.5 py-0.5">Inactive</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{a.email || a.phone || "—"}</div>
          <div className="text-[10px] font-bold text-slate-500 mt-1">
            {a.reports_count || 0} report{a.reports_count === 1 ? "" : "s"}
            {" · "}{a.showings_count || 0} showing{a.showings_count === 1 ? "" : "s"}
            {a.last_seen_at ? ` · last seen ${a.last_seen_at.slice(5, 16).replace("T", " ")}` : " · never opened"}
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            <button onClick={copyLink}
              className="h-8 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest"
              data-testid={`landlord-leasing-copy-${a.id}`}>📋 Copy Link</button>
            <a href={smsHref} data-testid={`landlord-leasing-sms-${a.id}`}
              className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center">💬 SMS</a>
            <a href={mailHref} data-testid={`landlord-leasing-email-${a.id}`}
              className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center">✉️ Email</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function LandlordLeasingFeed({ feed }) {
  const rows = [
    ...(feed.reports || []).map(r => ({ ...r, _kind: "report", _when: r.at })),
    ...(feed.showings || []).map(s => ({ ...s, _kind: "showing", _when: s.checked_in_at })),
    ...(feed.messages || []).map(m => ({ ...m, _kind: "message", _when: m.at })),
  ].sort((a, b) => (b._when || "").localeCompare(a._when || "")).slice(0, 20);
  if (!rows.length) return <div className="text-xs text-slate-400 italic mt-2">No activity yet.</div>;
  return (
    <div className="mt-2 space-y-1 max-h-[60vh] overflow-y-auto" data-testid="landlord-leasing-feed">
      {rows.map((row, i) => (
        <div key={i} className="text-xs bg-white border border-slate-200 p-2 flex items-start gap-2">
          <span className={`shrink-0 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 ${
            row._kind === "report" ? "bg-orange-100 text-orange-700"
            : row._kind === "showing" ? "bg-cyan-100 text-cyan-700"
            : "bg-slate-100 text-slate-600"}`}>{row._kind}</span>
          <div className="flex-1 min-w-0">
            <div className="truncate">
              <span className="font-bold">{row.agent_name}</span>
              {" · "}
              {row._kind === "report" ? <>{row.title} <span className="text-slate-500">at {row.address}{row.unit ? ` #${row.unit}` : ""}</span></>
                : row._kind === "showing" ? <>checked in {row.prospect_name ? `with ${row.prospect_name}` : ""}</>
                : <span className="text-slate-700 italic">"{row.text}"</span>}
            </div>
            <div className="text-[10px] text-slate-400">{row._when?.slice(0, 16).replace("T", " ")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LandlordLeasingAddModal({ token, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/landlord/${token}/leasing/agents`,
        { name, email, phone, notes });
      setResult(r.data.agent);
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    if (!result?.url) return;
    try { await navigator.clipboard.writeText(result.url); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-slate-900 max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="landlord-leasing-add-modal">
        {!result ? (
          <>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Invite leasing agent</div>
            <h3 className="font-display font-black text-xl uppercase tracking-tight mt-1">Add someone to your leasing team</h3>
            <p className="text-xs text-slate-500 mt-2 mb-4">
              They'll get a private link that shows only <strong>your</strong> vacant units — entry codes, alarm codes, lockbox, WiFi, plus a Report button that opens a work order and notifies PMR. No more texting codes.
            </p>
            <div className="space-y-3">
              <label className="block">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Name *</div>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bilal Khan"
                  className="w-full h-10 px-3 border-2 border-slate-300 text-sm" data-testid="landlord-leasing-add-name"/>
              </label>
              <label className="block">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Email</div>
                <input value={email} onChange={e => setEmail(e.target.value)} placeholder="bilal@…"
                  className="w-full h-10 px-3 border-2 border-slate-300 text-sm" data-testid="landlord-leasing-add-email"/>
              </label>
              <label className="block">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Phone</div>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="215-555-…"
                  className="w-full h-10 px-3 border-2 border-slate-300 text-sm" data-testid="landlord-leasing-add-phone"/>
              </label>
              <label className="block">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Notes (optional)</div>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder="Anything the office should know"
                  className="w-full px-3 py-2 border-2 border-slate-300 text-sm" data-testid="landlord-leasing-add-notes"/>
              </label>
              <div className="flex gap-2 mt-4">
                <button onClick={onClose} className="flex-1 h-11 bg-slate-100 hover:bg-slate-200 font-black uppercase text-xs tracking-widest">Cancel</button>
                <button onClick={submit} disabled={busy}
                  className="flex-1 h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-50"
                  data-testid="landlord-leasing-add-submit">
                  {busy ? "Creating…" : "Create Link"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div data-testid="landlord-leasing-add-result">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">✓ Link ready</div>
            <h3 className="font-display font-black text-xl uppercase tracking-tight mt-1">{result.name}'s portal</h3>
            <div className="mt-3 p-3 bg-slate-50 border-2 border-slate-300">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Shareable URL</div>
              <div className="text-xs font-mono text-slate-800 break-all">{result.url}</div>
              <button onClick={copy}
                className="mt-2 h-9 px-3 bg-slate-900 hover:bg-black text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1"
                data-testid="landlord-leasing-add-copy">📋 Copy Link</button>
            </div>
            <div className="text-xs text-slate-500 mt-3">
              Text or email this to {result.name}. They can save it to their phone home screen for one-tap access.
            </div>
            <button onClick={onClose} className="mt-4 w-full h-11 bg-slate-100 hover:bg-slate-200 font-black uppercase text-xs tracking-widest">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

